import type { ReactNode } from 'react'

import { Dialog } from '@/ui/Dialog'
import { cn } from '@/ui/lib/utils'
import { QuantParamRow, type QuantParamKind } from '@/ui/QuantParamRow'

/** The six indicator colours (handoff 4.5). */
export const QUANT_INDICATOR_SWATCHES = ['#E0A030', '#F2EFE6', '#6FA8DC', '#7CB342', '#D0342C', '#B38CE0'] as const

export interface QuantIndicatorPickGroup {
  group: string
  items: Array<{ type: string; label: string; kind: string; description: string }>
}

export interface QuantIndicatorField {
  name: string
  /** Shown label and the control's accessible name (`Length`). */
  label: string
  kind: QuantParamKind
  value: string | boolean
  options?: string[]
  error?: string
}

export interface QuantIndicatorModalProps {
  /** Pick mode (the catalog) or edit mode (one indicator's settings). */
  mode: 'pick' | 'edit'
  title: string
  onClose: () => void
  /** Pick mode: grouped catalog, or a loading / error body. */
  groups?: QuantIndicatorPickGroup[]
  onPick?: (type: string) => void
  /** Shown in place of the catalog (loading, or an error with its own Retry). */
  placeholder?: ReactNode
  /** Edit mode. */
  typeLine?: string
  source?: string
  name?: string
  onName?: (next: string) => void
  fields?: QuantIndicatorField[]
  onField?: (name: string, next: string | boolean) => void
  color?: string
  onColor?: (hex: string) => void
  /** True when editing an existing indicator (Remove) rather than adding one (Back). */
  existing?: boolean
  onRemove?: () => void
  onBack?: () => void
  onSave?: () => void
  saveLabel?: string
}

const OUTLINE_BTN =
  'cursor-pointer border border-ink-25 bg-transparent px-3.5 py-2 font-sans text-[10px] font-semibold tracking-[0.2em] text-ink uppercase hover:bg-accent-wash'

/**
 * The Manual trade indicator modal (spec 036, handoff 4.5), built on the
 * shared `Dialog` (440px, 60% backdrop, backdrop-click closes). Pick mode lists
 * the catalog grouped by source; edit mode shows name, one param row per
 * param, six colour swatches and the footer actions. Props only.
 */
export function QuantIndicatorModal(props: QuantIndicatorModalProps) {
  const {
    mode,
    title,
    onClose,
    groups = [],
    onPick,
    placeholder,
    typeLine,
    source,
    name = '',
    onName,
    fields = [],
    onField,
    color,
    onColor,
    existing = false,
    onRemove,
    onBack,
    onSave,
    saveLabel,
  } = props
  return (
    <Dialog
      open
      titleId="quant-indicator-modal-title"
      title={title}
      onClose={onClose}
      widthClassName="max-w-[440px]"
      backdropClassName="bg-ink/60"
      closeOnBackdrop
      className="gap-3"
    >
      {mode === 'pick' ? (
        placeholder ?? (
          <div className="-mx-4 flex min-h-0 flex-col pb-2">
            {groups.map((g) => (
              <div key={g.group}>
                <div className="px-4 pt-3 pb-1 font-mono text-[9.5px] tracking-[0.2em] text-ink-50 uppercase">{g.group}</div>
                {g.items.map((item) => (
                  <button
                    key={item.type}
                    type="button"
                    onClick={() => onPick?.(item.type)}
                    className="flex w-full cursor-pointer flex-col gap-[3px] border-0 border-t border-ink-14 bg-transparent px-4 py-2.5 text-left text-ink hover:bg-accent-wash"
                  >
                    <span className="flex justify-between gap-2.5">
                      <span className="font-mono text-[12.5px] font-semibold">{item.label}</span>
                      <span className="font-mono text-[9.5px] tracking-[0.14em] text-ink-50 uppercase">{item.kind}</span>
                    </span>
                    <span className="font-mono text-[11px] leading-[1.45] text-ink-50">{item.description}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <div className="flex justify-between gap-2.5 font-mono text-[10px] tracking-[0.14em] text-ink-50 uppercase">
              <span>{typeLine}</span>
              <span>{source}</span>
            </div>
            <label className="flex flex-col gap-[5px]">
              <span className="font-mono text-[11px] font-semibold">Name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => onName?.(event.target.value)}
                className="border border-ink-25 bg-transparent px-[9px] py-[7px] font-mono text-[12.5px] text-ink outline-none focus:border-accent-fill"
              />
            </label>
            {fields.map((f) => (
              <QuantParamRow
                key={f.name}
                variant="modal"
                name={f.label}
                kind={f.kind}
                value={f.value}
                options={f.options}
                error={f.error}
                changed={false}
                defaultLabel=""
                onChange={(next) => onField?.(f.name, next)}
              />
            ))}
            <div role="group" aria-label="Color" className="flex flex-col gap-1.5">
              <span className="font-mono text-[11px] font-semibold">Color</span>
              <div className="flex gap-1.5">
                {QUANT_INDICATOR_SWATCHES.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    title={hex}
                    aria-label={`Color ${hex}`}
                    aria-pressed={color === hex}
                    onClick={() => onColor?.(hex)}
                    style={{ background: hex }}
                    className={cn(
                      'h-[26px] w-[26px] cursor-pointer p-0',
                      color === hex ? 'border-2 border-ink outline-2 -outline-offset-4 outline-paper' : 'border border-ink-25',
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 border-t border-ink-20 pt-3">
            {existing ? (
              <button
                type="button"
                onClick={onRemove}
                className="cursor-pointer border border-alert bg-transparent px-3 py-2 font-sans text-[10px] font-semibold tracking-[0.2em] text-alert uppercase"
              >
                Remove
              </button>
            ) : (
              <button type="button" onClick={onBack} className={cn(OUTLINE_BTN, 'px-3')}>
                ← Back
              </button>
            )}
            <span className="flex-1" />
            <button type="button" onClick={onClose} className={OUTLINE_BTN}>
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              className="cursor-pointer border border-ink bg-ink px-4 py-2 font-sans text-[10px] font-bold tracking-[0.2em] text-paper uppercase"
            >
              {saveLabel}
            </button>
          </div>
        </>
      )}
    </Dialog>
  )
}
